import { FC, RefObject } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Unstable_Grid2";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme as MuiTheme } from "@mui/material/styles/createTheme";
import { useTranslation } from "react-i18next";

interface HomeBenefitsProps {
  targetRef: RefObject<HTMLDivElement>;
}

export const HomeBenefits: FC<HomeBenefitsProps> = ({ targetRef }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const smDown = useMediaQuery((theme: MuiTheme) =>
    theme.breakpoints.down("sm")
  );

  const benefits = [
    t("Built by people living with diabetes"),
    t("Fast and continuously improved"),
    t("Adaptable to your lifestyle"),
    t("Choose your own combination of CGM and pump"),
    t("No commercial trade secrets"),
    t("100% yours—no data locking"),
  ];
  const healthOutcomeStudies = [
    {
      title: "The do-it-yourself artificial pancreas: a comprehensive review",
      url: "https://link.springer.com/article/10.1007/s13300-020-00823-z",
    },
    {
      title:
        "Efficacy, safety, and user experience of DIY or open-source artificial pancreas systems: a systematic review",
      url: "https://link.springer.com/article/10.1007/s00592-020-01623-4",
    },
    {
      title:
        "A scoping review of Do-It-Yourself Automated Insulin Delivery system (DIY AID) use in people with type 1 diabetes",
      url: "https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0271096",
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.common.white,
        py: { xs: 10, md: 14 },
      }}
      ref={targetRef}
    >
      <Container maxWidth="lg">
        <Grid
          container
          spacing={{ xs: 5, md: 8 }}
          alignItems="flex-start"
          sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 5 }}
        >
          <Grid xs={12} md={6}>
            <Typography
              component="p"
              variant="overline"
              sx={{
                color: theme.palette.text.secondary,
                letterSpacing: "0.08em",
              }}
            >
              {t("Why open-source")}
            </Typography>

            <Typography
              component="h2"
              variant={smDown ? "h4" : "h3"}
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 700,
                lineHeight: 1.2,
                mt: 1,
                mb: 3,
              }}
            >
              {t("Key benefits of using open-source")}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 560,
                lineHeight: 1.7,
                mb: 4,
              }}
            >
              {/* Open-source diabetes tools stay practical and trustworthy because
              the community can inspect, improve, and adapt them in real-world
              use. */}
            </Typography>

            <Stack spacing={2.25}>
              {benefits.map((benefit, index) => (
                <Typography
                  key={index}
                  variant="body1"
                  sx={{
                    color: theme.palette.text.primary,
                    lineHeight: 1.65,
                    pl: 2.5,
                    position: "relative",
                    "&::before": {
                      content: '"\u2013"',
                      position: "absolute",
                      left: 0,
                      color: theme.palette.text.secondary,
                    },
                  }}
                >
                  {benefit}
                </Typography>
              ))}
            </Stack>
          </Grid>

          <Grid xs={12} md={6}>
            <Box
              sx={{
                pl: { md: 6 },
                borderLeft: {
                  xs: "none",
                  md: `1px solid ${theme.palette.divider}`,
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  my: 2.5,
                }}
              >
                {t("Health outcomes")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  mb: 3,
                  lineHeight: 1.6,
                  maxWidth: 520,
                }}
              >
                {t(
                  "Multiple studies on open-source looping report positive effects on glycemic outcomes, safety, and quality of life."
                )}
                <Box
                  component="sup"
                  sx={{
                    fontSize: "0.75em",
                    lineHeight: 1,
                    ml: 0.35,
                  }}
                >
                  {healthOutcomeStudies.map((study, index) => (
                    <Box
                      key={`text-ref-${study.url}`}
                      component="a"
                      href={study.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: theme.palette.text.secondary,
                        textDecorationColor: theme.palette.divider,
                        "&:hover": {
                          color: theme.palette.text.primary,
                          textDecorationColor: "currentColor",
                        },
                        mr: index < healthOutcomeStudies.length - 1 ? 0.3 : 0,
                      }}
                    >
                      {index + 1}
                      {index < healthOutcomeStudies.length - 1 ? "," : ""}
                    </Box>
                  ))}
                </Box>
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: { xs: 2, sm: 3 },
                  pt: 3,
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box>
                  <Typography
                    variant={smDown ? "h5" : "h4"}
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      mb: 0.5,
                    }}
                  >
                    {">70%"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {t("Time-in-range")}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant={smDown ? "h5" : "h4"}
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      mb: 0.5,
                    }}
                  >
                    {"<6.5%"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                  >
                    HbA1c
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant={smDown ? "h5" : "h4"}
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      mb: 0.5,
                    }}
                  >
                    {t("Improved")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {t("Quality of life")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
